import { FC } from "react";
import {
  IRSupportData,
  IStockTechnicalSummaryData,
} from "../../interfaces/StockTechnicalSummary.interface";
import StockTable from "./StockTable";
import {
  STOCK_TECHNICAL_SUMMARY_KEYS,
  TECHNICAL_ORDER,
} from "./StockTechnicalSummaryConstant";

interface TechnicalSummaryTableProps {
  selectedTab: keyof IStockTechnicalSummaryData;
  data: IStockTechnicalSummaryData[keyof IStockTechnicalSummaryData];
}

// Utility: Interleave two arrays
const interleave = <T,>(arr1: T[], arr2: T[]): T[] => {
  const result: T[] = [];
  const max = Math.max(arr1.length, arr2.length);
  for (let i = 0; i < max; i++) {
    if (arr1[i]) result.push(arr1[i] as T);
    if (arr2[i]) result.push(arr2[i] as T);
  }
  return result;
};

const TechnicalSummaryTable: FC<TechnicalSummaryTableProps> = ({
  selectedTab,
  data = {},
}) => {
  const tab = selectedTab.toLowerCase();
  const typedData = data as { price?: number } | null | undefined;
  const priceValue = typedData && "price" in typedData ? typedData?.price : 0;

  const entries = Object.entries(data || {}).flatMap(([key, value]) => {
    const label =
      STOCK_TECHNICAL_SUMMARY_KEYS[
        key as keyof typeof STOCK_TECHNICAL_SUMMARY_KEYS
      ];

    if (!label || key === "pivot") return [];

    const safeValue =
      typeof value === "string" || typeof value === "number" || typeof value === "undefined"
        ? value
        : undefined;

    return {
      label,
      value: safeValue,
      ...(tab === "pchganalysis" && {
        icon: typeof safeValue === "number" && safeValue > 0 ? "▲" : "▼",
        color: typeof safeValue === "number" && safeValue > 0 ? "text-green-500" : "text-red-600",
      }),
    };
  });

  // Remove duplicates by label
  const uniqueEntries = entries.filter(
    (item, index, self) =>
      index === self.findIndex((o) => o.label === item.label),
  );

  // Split entries
  const supportResistance = uniqueEntries.filter(
    (e) =>
      e.label.toLowerCase().includes("support") ||
      e.label.toLowerCase().includes("resistance"),
  );
  const others = uniqueEntries.filter((e) => !supportResistance.includes(e));

  // --- Handle Support/Resistance ---
  const orderedSupportResistance =
    tab === "rsupport"
      ? (() => {
          const support = supportResistance.filter((i) =>
            i.label.toLowerCase().includes("support"),
          );
          const resistance = supportResistance.filter((i) =>
            i.label.toLowerCase().includes("resistance"),
          );
          return interleave(support, resistance);
        })()
      : supportResistance;

  // --- Handle Others ---
  let orderedOthers: typeof others = [];

  if (tab === "pchganalysis") {
    const months = others.filter((o) =>
      o.label.toLowerCase().includes("month"),
    );
    const years = others.filter((o) => o.label.toLowerCase().includes("year"));
    const interleaved = interleave(months, years);
    const used = new Set(interleaved.map((i) => i.label));
    const remaining = others.filter((o) => !used.has(o.label));
    orderedOthers = [...interleaved, ...remaining];
  } else if (["ema", "sma", "beta", "dnvol"].includes(tab)) {
    orderedOthers = others;
  } else {
    orderedOthers = TECHNICAL_ORDER.map((key) => {
      const label =
        STOCK_TECHNICAL_SUMMARY_KEYS[
          key as keyof typeof STOCK_TECHNICAL_SUMMARY_KEYS
        ];
      return others.find((item) => item.label === label);
    }).filter(Boolean) as typeof others;
  }

  return (
    <div className="mt-4 md:mt-3">
      <div className={tab === "rsupport" && (data as IRSupportData)?.pivot ? "block" : "hidden"}>
        {typeof (data as IRSupportData)?.pivot !== "undefined" && (data as IRSupportData)?.pivot !== null && (
          <>
            <h2 className="text-center text-[18px] text-xl leading-[21.33px] text-black md:text-[28px] md:font-[500] md:leading-[33.18px]">
              {(data as IRSupportData)?.pivot}
            </h2>
            <h3 className="pb-4 text-center text-[14px] leading-[20px] text-black md:text-[20px] md:leading-[23.7px]">
              PIVOT
            </h3>
          </>
        )}
      </div>


      {orderedSupportResistance.length > 0 && (
        <StockTable
          entries={orderedSupportResistance}
          label={selectedTab}
          priceValue={priceValue}
        />
      )}

      {orderedOthers.length > 0 && (
        <StockTable
          entries={orderedOthers}
          label={selectedTab}
          priceValue={priceValue}
        />
      )}
    </div>
  );
};

export default TechnicalSummaryTable;
